import { useDebounceCallback } from '@portal/shared/hooks/useDebounceCallback';
import { assertNever } from '@portal/types/helpers';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Index of items determines how they're stacked when multiple are displayed at
 * once.
 */
export const HEADER_TYPES = [
  'skip-button',
  'offer-anchor',
  'pre-header',
  'header',
  'pre-navigation',
  'navigation'
] as const;
const STICKY_HEADER_TYPE = 'sticky-header';
export type ScrollPaddingTopHeaderType = (typeof HEADER_TYPES)[number] | typeof STICKY_HEADER_TYPE;

type ScrollPaddingTop = {
  [headerType in ScrollPaddingTopHeaderType]?: number;
};

interface ScrollPaddingTopProps {
  type: ScrollPaddingTopHeaderType;
  value: number;
}

const scrollPaddingTopAtomBase = atom<ScrollPaddingTop>({});
scrollPaddingTopAtomBase.debugLabel = 'scrollPaddingTopAtomBase';

export const scrollPaddingTopAtom = atom(
  (get) => get(scrollPaddingTopAtomBase),
  (get, set, { type, value }: ScrollPaddingTopProps) => {
    const oldValue = get(scrollPaddingTopAtomBase);
    const newValue = {
      ...oldValue,
      [type]: value
    };

    if (typeof document !== 'undefined') {
      const documentRoot = document.querySelector<HTMLElement>(':root');
      if (documentRoot) {
        // STICKY_HEADER_TYPE is treated as an override for all other headers
        // when present, as they are used to wrap other headers. Don't include
        // them in the total height.
        let heightSum = newValue[STICKY_HEADER_TYPE] || 0;
        if (heightSum <= 0) {
          const nonStickyHeaders = { ...newValue, [STICKY_HEADER_TYPE]: 0 };
          heightSum = Object.values(nonStickyHeaders)?.reduce((a, b) => a + b, 0) || 0;
        }
        documentRoot.style.scrollPaddingTop = `${heightSum}px`;
      }
    }

    set(scrollPaddingTopAtomBase, newValue);
  }
);

scrollPaddingTopAtom.debugLabel = 'scrollPaddingTopAtom';

export const getScrollPaddingTopCSSVariable = (type: ScrollPaddingTopHeaderType) =>
  `--${type}-height`;

const getCSSValue = (type: ScrollPaddingTopHeaderType, fallback: number = 0) => {
  return `var(${getScrollPaddingTopCSSVariable(type)}, ${fallback}px)`;
};

export function useScrollPaddingTopForType(
  type: ScrollPaddingTopHeaderType | null,
  format: 'css'
): string | number;
export function useScrollPaddingTopForType(
  type: ScrollPaddingTopHeaderType | null,
  format: 'raw'
): number;
export function useScrollPaddingTopForType(
  type: ScrollPaddingTopHeaderType | null,
  format: 'css' | 'raw'
) {
  const scrollPaddingTop = useAtomValue(scrollPaddingTopAtom);
  const includesAll = type === null || type === STICKY_HEADER_TYPE;
  const index = includesAll ? HEADER_TYPES.length : HEADER_TYPES.indexOf(type);
  if (index <= 0) {
    // Top-most item doesn't need to be offset.
    return 0;
  }
  const typesAboveSelf = HEADER_TYPES.slice(0, index);
  if (typesAboveSelf.length === 0) {
    return 0;
  }

  if (includesAll && scrollPaddingTop[STICKY_HEADER_TYPE]) {
    if (format === 'css') {
      return getCSSValue(STICKY_HEADER_TYPE);
    }
    if (format === 'raw') {
      return scrollPaddingTop[STICKY_HEADER_TYPE] || 0;
    }
    return assertNever(format);
  }

  if (format === 'css') {
    return `calc(${typesAboveSelf.map((t) => getCSSValue(t)).join(' + ')})`;
  }
  if (format === 'raw') {
    return typesAboveSelf.reduce((acc, t) => acc + (scrollPaddingTop[t] || 0), 0);
  }
  return assertNever(format);
}

/**
 * Sets up resize observers and updates the scroll padding top value for the
 * given header type. Automatically cleans up when the component unmounts when
 * {@link disable} is `false`.
 *
 * @returns `height` for header placeholders and `top` for fixed headers.
 */
export const useScrollPaddingTopHandler = (
  {
    type,
    disable,
    noDebounce
  }: {
    type: ScrollPaddingTopHeaderType;
    disable?: boolean;
    noDebounce?: boolean;
  },
  primaryRef: React.RefObject<HTMLElement | null>,
  secondaryRef?: React.RefObject<HTMLElement | null>
) => {
  const setScrollTop = useSetAtom(scrollPaddingTopAtom);
  const refs = useMemo(
    () => [primaryRef, ...(secondaryRef ? [secondaryRef] : [])],
    [primaryRef, secondaryRef]
  );

  const [height, setHeight] = useState(
    refs.reduce((acc, ref) => (ref.current ? (ref.current?.offsetHeight || 0) + acc : acc), 0)
  );
  const heightBootstrapped = useRef(false);

  const onResize = useCallback((entries: ResizeObserverEntry[]) => {
    let newHeight = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (const entry of entries) {
      if (entry) {
        newHeight +=
          entry.borderBoxSize.find(Boolean)?.blockSize ||
          (entry.target as HTMLElement).offsetHeight ||
          0;
      }
    }
    setHeight(newHeight);
    if (newHeight <= 0) {
      heightBootstrapped.current = false;
    }
  }, []);

  const debounceResize = useDebounceCallback(onResize, 50);

  const resizeObserverCallback = useRef(onResize);
  resizeObserverCallback.current =
    noDebounce || !heightBootstrapped.current ? onResize : debounceResize;

  useEffect(() => {
    if (!refs.length || !refs.some((i) => i.current)) {
      return () => {};
    }

    const observer = new ResizeObserver((entries) => resizeObserverCallback.current(entries));

    // eslint-disable-next-line no-restricted-syntax
    for (const ref of refs) {
      if (ref.current) {
        observer.observe(ref.current);
      }
    }
    return () => observer.disconnect();
  }, [refs]);

  useEffect(() => {
    // Don't clean up if we're disabling the effect.
    if (disable) {
      return () => {};
    }

    setScrollTop({ type, value: height });
    return () => {
      setScrollTop({ type, value: 0 });
    };
  }, [height, setScrollTop, disable, type]);

  const top = useScrollPaddingTopForType(type, 'css');

  return useMemo(() => ({ height, top }), [height, top]);
};
